'use client';

import { useEffect, useState } from 'react';

/**
 * V2.11 J5 (2026-05-20) — "Add to home screen" prompt for mobile users.
 *
 * Two paths:
 *   - iOS Safari: there's no beforeinstallprompt event; we show a
 *     dismissible banner explaining the share-sheet → "添加到主屏幕"
 *     flow that's the only way to install on iOS.
 *   - Android Chrome / Edge: we listen for `beforeinstallprompt` and
 *     show a button that fires the native prompt when tapped.
 *
 * The banner stays dismissed for 30 days via localStorage. When the
 * app is already running standalone (PWA installed), we never render
 * the banner — `display-mode: standalone` matches a media query for
 * Chrome / Edge / Safari iOS 16.4+.
 */

const STORAGE_KEY = 'cea_install_hint_dismissed_at';
const DISMISSED_TTL_MS = 30 * 24 * 3600 * 1000; // 30 days

type Mode = 'hidden' | 'ios' | 'android';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (!isIOS) return false;
  // Exclude in-app browsers (WeChat, Weibo, Line, etc.) — A2HS doesn't
  // work in those; showing the hint would just confuse users.
  if (/MicroMessenger|Weibo|Line|FBAN|FBAV|Instagram/i.test(ua)) return false;
  // Crios = Chrome on iOS, FxiOS = Firefox on iOS — both can A2HS too
  // (via their own share sheet on iOS 17+), close enough to native Safari.
  return /Safari/i.test(ua);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // matchMedia is the cross-browser way; iOS legacy `navigator.standalone`
  // covers iOS 13-15 Safari pre-matchMedia support.
  const mq = window.matchMedia?.('(display-mode: standalone)').matches;
  // biome-ignore lint/suspicious/noExplicitAny: legacy iOS prop
  const iosLegacy = (navigator as any).standalone === true;
  return mq || iosLegacy;
}

function recentlyDismissed(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (!v) return false;
    const at = Number.parseInt(v, 10);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < DISMISSED_TTL_MS;
  } catch {
    return false;
  }
}

export function InstallHint(): JSX.Element | null {
  const [mode, setMode] = useState<Mode>('hidden');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Bail fast if standalone, not mobile, or recently dismissed.
    if (isStandalone() || !isMobile() || recentlyDismissed()) {
      setMode('hidden');
      return;
    }

    if (isIOSSafari()) {
      setMode('ios');
      return;
    }

    // Android Chrome / Edge path: listen for beforeinstallprompt. The
    // event fires when the browser has decided this site is installable
    // (PWA criteria met). We stash it and show our own button.
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setMode('android');
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function dismiss() {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      /* localStorage blocked — no-op */
    }
    setMode('hidden');
  }

  async function triggerAndroidInstall() {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') dismiss();
    } catch {
      /* user cancelled / browser quirk — silently noop */
    }
  }

  if (mode === 'hidden') return null;

  // Native <dialog> requires imperative .show()/.close() + backdrop
  // styling we don't want here; this is a passive bottom-sheet banner.
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: passive banner, see comment above
      role="dialog"
      aria-label="安装到主屏幕"
      // Sit above the MobileBottomNav (56px + safe-area-inset-bottom)
      // on phones; collapse to the right corner on desktop.
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 68px)' }}
      className="fixed left-3 right-3 z-50 rounded-2xl border border-soft bg-white shadow-lg px-4 py-3 flex items-start gap-3 text-ink sm:max-w-md sm:left-auto sm:right-3 md:!bottom-3"
    >
      <div className="w-10 h-10 rounded-xl bg-cream flex-shrink-0 flex items-center justify-center">
        {/* Reuse the existing PWA icon as the install affordance */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/icons/apple-icon-180.png" alt="" width={32} height={32} className="rounded-lg" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[15px] mb-0.5">把首席 AI 放到主屏幕</div>
        {mode === 'ios' ? (
          <p className="text-xs text-dusk leading-relaxed">
            点击底部
            <ShareIcon className="inline align-text-bottom mx-0.5 w-3.5 h-3.5" />
            分享按钮 → 选「添加到主屏幕」，下次直接从桌面打开，速度更快也不挤浏览器。
          </p>
        ) : (
          <p className="text-xs text-dusk leading-relaxed">
            点下面「安装」一键添加到主屏幕，下次直接打开，速度更快。
          </p>
        )}
        {mode === 'android' && (
          <button
            type="button"
            onClick={triggerAndroidInstall}
            className="mt-2 text-sm px-3 py-1.5 rounded-lg bg-warmth-dark text-white hover:bg-warmth transition"
          >
            安装
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="关闭"
        className="text-dusk hover:text-ink text-base leading-none -mt-1 -mr-1 p-1"
      >
        ×
      </button>
    </div>
  );
}

function ShareIcon({ className = '' }: { className?: string }): JSX.Element {
  // iOS share icon — square + up-arrow. SVG so it scales with text color.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role="img"
      aria-label="分享图标"
    >
      <title>分享</title>
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}
